import { Autocomplete, TextField, createFilterOptions } from "@mui/material";
import { useMemo, useState } from "react";
import { type Project, useAppData } from "store/appData";
import { regionKey } from "utils/regionKey";
import {
  formatProjectLabel,
  useDatasetViewerSettings,
} from "views/DatasetViewer/hooks/useDatasetViewerSettings";
import t from "./translations";

type Props = {
  selectedProjectReferences: string[];
  onAdd: (projectReference: string) => void;
};

const filterProjects = createFilterOptions<Project>({
  stringify: (project) =>
    `${project.region}${project.id} ${project.region}-${project.id} ${project.id} ${project.name}`,
});

export default function ProjectPicker({
  selectedProjectReferences,
  onAdd,
}: Props) {
  const { projectList: projects } = useAppData();
  const { projectNameDisplay } = useDatasetViewerSettings();
  const projectLabel = (project: Project) =>
    formatProjectLabel(project, projectNameDisplay);
  const [searchText, setSearchText] = useState("");

  const addableProjects = useMemo(() => {
    const selectedReferences = new Set(selectedProjectReferences);
    return projects.filter(
      (project) =>
        !selectedReferences.has(regionKey(project.region, project.id))
    );
  }, [projects, selectedProjectReferences]);

  return (
    <Autocomplete<Project>
      size="small"
      noOptionsText={t.NoProjectOptions}
      options={addableProjects}
      value={null}
      inputValue={searchText}
      onInputChange={(_event, nextSearchText, reason) => {
        if (reason !== "reset") setSearchText(nextSearchText);
      }}
      getOptionLabel={projectLabel}
      getOptionKey={(project) => regionKey(project.region, project.id)}
      filterOptions={filterProjects}
      onChange={(_event, project) => {
        if (!project) return;
        onAdd(regionKey(project.region, project.id));
        setSearchText("");
      }}
      renderOption={({ key, ...optionProps }, project) => (
        <li
          key={key}
          {...optionProps}
          data-testid={`lidar-upload-status.project-option.${regionKey(project.region, project.id)}`}
        >
          {projectLabel(project)}
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={t.AddProject}
          slotProps={{
            ...params.slotProps,
            htmlInput: {
              ...params.slotProps.htmlInput,
              "data-testid": "lidar-upload-status.project-picker",
            },
          }}
        />
      )}
      sx={{ width: 420 }}
    />
  );
}
